import { setBodyOverflow, unsetBodyOverflow } from '@/utils/dom'
import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
    static targets = ['content', 'mobileControl']

    declare readonly contentTarget: HTMLElement
    declare readonly hasContentTarget: boolean
    declare readonly mobileControlTarget: HTMLElement
    declare readonly hasMobileControlTarget: boolean

    private isDragging = false
    private isClosing = false
    private startY = 0
    private currentY = 0
    private contentHeight = 0
    private windowHeight = 0
    private dragThreshold = 0.2

    connect() {
        this.element.addEventListener('modal:set-content', this.handleSetContent as EventListener)
        this.element.addEventListener('modal:open', this.open as EventListener)
        this.element.addEventListener('modal:hide', this.hide as EventListener)
        this.element.addEventListener('modal:toggle', this.toggle as EventListener)
        this.element.addEventListener('click', this.handleClick)

        document.addEventListener('keydown', this.handleKeydown)
        setTimeout(() => this.element.classList.add('inited'), 500)
    }

    disconnect() {
        this.element.removeEventListener('modal:set-content', this.handleSetContent as EventListener)
        this.element.removeEventListener('modal:open', this.open as EventListener)
        this.element.removeEventListener('modal:hide', this.hide as EventListener)
        this.element.removeEventListener('modal:toggle', this.toggle as EventListener)
        this.element.removeEventListener('click', this.handleClick as EventListener)

        document.removeEventListener('keydown', this.handleKeydown)
    }

    mobileControlTargetConnected(element: HTMLElement) {
        element.addEventListener('touchstart', this.handleTouchStart, { passive: false })
        element.addEventListener('touchmove', this.handleTouchMove, { passive: false })
        element.addEventListener('touchend', this.handleTouchEnd, { passive: false })
        element.addEventListener('mousedown', this.handleMouseDown)
    }

    mobileControlTargetDisconnected(element: HTMLElement) {
        element.removeEventListener('touchstart', this.handleTouchStart)
        element.removeEventListener('touchmove', this.handleTouchMove)
        element.removeEventListener('touchend', this.handleTouchEnd)
        element.removeEventListener('mousedown', this.handleMouseDown)
    }

    open = (): Promise<any> => {
        const promise = new Promise((resolve) => this.element.addEventListener('transitionend', () => resolve(null), { once: true }))

        setBodyOverflow()

        this.element.classList.add('show')
        this.contentTarget.style.transform = ''
        this.isClosing = false

        return promise
    }

    hide = (): Promise<any> => {
        const promise = new Promise((resolve) => this.element.addEventListener('transitionend', () => resolve(null), { once: true }))

        this.element.classList.remove('show')
        this.isClosing = false
        this.contentTarget.style.transform = ''

        unsetBodyOverflow()

        return promise
    }

    toggle = () => {
        this.isOpen() ? this.hide() : this.open()
    }

    setContent(html: string) {
        if (!this.hasContentTarget) {
            return
        }

        this.contentTarget.innerHTML = html
    }

    private isOpen(): boolean {
        return this.element.classList.contains('show')
    }

    private handleClick = (event: Event) => {
        const target = event.target as HTMLElement

        if (!this.isOpen()) {
            return
        }

        if (this.contentTarget.contains(target) || target.parentNode === null) {
            return
        }

        this.hide()
    }

    private handleKeydown = (event: KeyboardEvent) => {
        if (event.key === 'Escape' && this.isOpen()) {
            this.hide()
        }
    }

    private handleSetContent = (event: CustomEvent) => {
        const html = event.detail && event.detail.html ? String(event.detail.html) : ''
        if (!html) {
            return
        }

        this.setContent(html)
    }

    private handleTouchStart = (event: TouchEvent) => {
        if (!this.isOpen() || window.innerWidth >= 768 || this.isClosing) {
            return
        }

        event.preventDefault()
        this.startDragging(event.touches[0].clientY)
    }

    private handleTouchMove = (event: TouchEvent) => {
        if (!this.isDragging || !this.isOpen() || window.innerWidth >= 768 || this.isClosing) {
            return
        }

        event.preventDefault()

        this.dragMove(event.touches[0].clientY)
    }

    private handleTouchEnd = (event: TouchEvent) => {
        if (!this.isDragging || !this.isOpen() || window.innerWidth >= 768) {
            return
        }

        event.preventDefault()
        this.dragEnd()
    }

    private handleMouseDown = (event: MouseEvent) => {
        if (!this.isOpen() || window.innerWidth >= 768 || this.isClosing) {
            return
        }

        event.preventDefault()

        this.startDragging(event.clientY)

        document.addEventListener('mousemove', this.handleMouseMove)
        document.addEventListener('mouseup', this.handleMouseUp)
    }

    private handleMouseMove = (event: MouseEvent) => {
        if (!this.isDragging || this.isClosing) {
            return
        }

        event.preventDefault()
        this.dragMove(event.clientY)
    }

    private handleMouseUp = (event: MouseEvent) => {
        if (!this.isDragging) {
            return
        }

        event.preventDefault()
        this.dragEnd()

        document.removeEventListener('mousemove', this.handleMouseMove)
        document.removeEventListener('mouseup', this.handleMouseUp)
    }

    private startDragging(startY: number) {
        this.isDragging = true
        this.startY = startY
        this.currentY = startY
        this.contentHeight = this.contentTarget.offsetHeight
        this.windowHeight = window.innerHeight

        this.element.classList.add('dragged')
    }

    private dragMove(clientY: number) {
        this.currentY = clientY
        const deltaY = this.currentY - this.startY

        const limitedDeltaY = Math.max(deltaY, 0)

        this.contentTarget.style.transform = `translateY(${limitedDeltaY}px)`

        const adaptiveThreshold = this.getCloseThreshold()

        if (deltaY > adaptiveThreshold && !this.isClosing) {
            this.startClosing()
        }
    }

    private dragEnd() {
        this.isDragging = false

        const deltaY = this.currentY - this.startY
        const adaptiveThreshold = this.getCloseThreshold()

        this.element.classList.remove('dragged')

        if (deltaY > adaptiveThreshold || this.isClosing) {
            this.continueClosing()
        } else {
            this.contentTarget.style.transform = 'translateY(0)'
        }
    }

    private getCloseThreshold(): number {
        const minHeight = Math.min(this.contentHeight, this.windowHeight)
        return minHeight * this.dragThreshold
    }

    private startClosing() {
        this.isClosing = true
    }

    private continueClosing() {
        if (this.isClosing) {
            this.contentTarget.style.transform = `translateY(${this.windowHeight}px)`
            setTimeout(() => this.hide(), 300)
        }
    }
}
